package dev.lingvo.site.state

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ProjectSummaryDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

private data class DirectoryRequest(
    val key: WorkspaceRequestKey,
    val generation: Long,
)

private data class DirectoryEntry(
    val projects: List<ProjectSummaryDto> = emptyList(),
    val loaded: Boolean = false,
    val attempted: Boolean = false,
    val error: String? = null,
    /**
     * The HTTP status behind [error], when there was one.
     *
     * Carried because `AsyncFailureState` reads it: without a status its refusal and missing
     * branches are unreachable, and a 401 or a 404 renders as a retryable fault with a button that
     * re-runs the request the server has already refused. `c8ef3e8` fixed that on the projects page
     * deliberately; a later refactor rewrapped a bare message and undid it.
     */
    val errorStatus: Int? = null,
    val verified: Boolean = false,
)

/**
 * A workspace- and session-keyed project directory.
 *
 * The loader is injected because the race is the behavior worth testing. The application singleton
 * below supplies the real API. A completion publishes only when it is still the latest request for
 * the active session/workspace; stale success, failure, and finally branches are all no-ops.
 */
internal class ProjectDirectoryStore(
    private val loader: suspend (String) -> List<ProjectSummaryDto>,
) {
    var projects: List<ProjectSummaryDto> by mutableStateOf(emptyList())
        private set

    var loading: Boolean by mutableStateOf(false)
        private set

    var error: String? by mutableStateOf(null)
    var errorStatus: Int? by mutableStateOf(null)
        private set

    private var activeKey: WorkspaceRequestKey? = null
    private var publishedKey: WorkspaceRequestKey? = null
    private val entries = mutableMapOf<WorkspaceRequestKey, DirectoryEntry>()
    private val generations = mutableMapOf<WorkspaceRequestKey, Long>()
    private val latestRequests = mutableMapOf<WorkspaceRequestKey, DirectoryRequest>()

    fun ensure(
        scope: CoroutineScope,
        context: WorkspaceRequestContext,
        force: Boolean = false,
    ) {
        activate(context.key)
        val entry = entries[context.key]
        val current = latestRequests[context.key]
        if (!force && current != null) return
        if (!force && entry?.attempted == true) return

        val generation = (generations[context.key] ?: 0L) + 1L
        generations[context.key] = generation
        val request = DirectoryRequest(context.key, generation)
        latestRequests[context.key] = request
        if (activeKey == context.key) loading = true

        scope.launch {
            try {
                val loaded = context.filterProjects(loader(context.workspace.slug))
                if (isCurrent(request)) {
                    val settled = DirectoryEntry(
                        projects = loaded,
                        loaded = true,
                        attempted = true,
                        verified = true,
                    )
                    entries[request.key] = settled
                    publish(request.key, settled)
                }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                if (isCurrent(request)) {
                    val previous = entries[request.key]
                    val failed = DirectoryEntry(
                        projects = previous?.projects.orEmpty(),
                        loaded = previous?.loaded == true,
                        attempted = true,
                        error = failure.message?.takeIf(String::isNotBlank)
                            ?: tr(
                                "state.project_directory.couldnt_load_your_projects",
                                "Couldn't load your projects.",
                            ),
                        errorStatus = (failure as? ApiException)?.status,
                        verified = previous?.verified == true,
                    )
                    entries[request.key] = failed
                    publish(request.key, failed)
                }
            } finally {
                if (latestRequests[request.key] == request) latestRequests.remove(request.key)
                if (activeKey == request.key && latestRequests[request.key] == null) loading = false
            }
        }
    }

    fun invalidate(context: WorkspaceRequestContext? = null) {
        val key = context?.key ?: activeKey ?: return
        generations[key] = (generations[key] ?: 0L) + 1L
        latestRequests.remove(key)
        entries[key] = entries[key]?.copy(loaded = false, attempted = false) ?: DirectoryEntry()
        if (activeKey == key) loading = false
    }

    fun seed(context: WorkspaceRequestContext, project: ProjectDetailDto): Boolean {
        val normalized = context.normalizeProject(project) ?: return false
        activate(context.key)
        val previous = entries[context.key] ?: DirectoryEntry()
        // Detail is newer than any directory request already in flight. Retire that request before
        // publication so its success, failure, and finally paths cannot replace this project.
        generations[context.key] = (generations[context.key] ?: 0L) + 1L
        latestRequests.remove(context.key)
        val summary = normalized.toSummary()
        val nextProjects = previous.projects
            .filterNot { it.id == summary.id || it.slug == summary.slug }
            .plus(summary)
        val seeded = previous.copy(
            projects = nextProjects,
            // A verified detail is not a verified *complete list*. Retry an earlier failed/invalidated
            // directory, while preserving a previously complete list updated by this detail.
            attempted = previous.loaded,
            error = null,
            errorStatus = null,
            verified = true,
        )
        entries[context.key] = seeded
        publish(context.key, seeded)
        loading = false
        return true
    }

    fun replace(project: ProjectDetailDto) {
        val key = activeKey ?: return
        val entry = entries[key] ?: return
        val normalized = if (project.orgSlug.isBlank()) project.copy(orgSlug = key.workspaceSlug) else project
        val index = entry.projects.indexOfFirst { it.id == normalized.id }
        if (
            index < 0 ||
            normalized.orgId != key.workspaceId ||
            normalized.orgSlug != key.workspaceSlug
        ) return
        val replaced = entry.projects.toMutableList().also { it[index] = normalized.toSummary() }
        val updated = entry.copy(projects = replaced)
        entries[key] = updated
        publish(key, updated)
    }

    fun find(slug: String?): ProjectSummaryDto? =
        slug?.let { wanted -> projects.firstOrNull { it.slug == wanted } }

    fun canAuthorize(context: WorkspaceRequestContext): Boolean =
        activeKey == context.key && publishedKey == context.key

    fun canVerifyAllowlist(context: WorkspaceRequestContext): Boolean =
        canAuthorize(context) && entries[context.key]?.loaded == true

    private fun activate(key: WorkspaceRequestKey) {
        if (activeKey == key) return
        activeKey = key
        val cached = entries[key]
        projects = cached?.projects.orEmpty()
        error = cached?.error
        errorStatus = cached?.errorStatus
        loading = latestRequests[key] != null
        publishedKey = key.takeIf { cached?.verified == true }
    }

    private fun publish(key: WorkspaceRequestKey, entry: DirectoryEntry) {
        if (activeKey != key) return
        projects = entry.projects
        error = entry.error
        errorStatus = entry.errorStatus
        publishedKey = key.takeIf { entry.verified }
    }

    private fun isCurrent(request: DirectoryRequest): Boolean =
        activeKey == request.key && latestRequests[request.key] == request
}

object ProjectDirectory {
    private val store = ProjectDirectoryStore(ApiClient::listProjects)

    val projects: List<ProjectSummaryDto> get() = store.projects
    val loading: Boolean get() = store.loading
    val error: String? get() = store.error

    /** The status behind [error], so a refusal can be told from a fault. */
    val errorStatus: Int? get() = store.errorStatus

    internal fun ensure(scope: CoroutineScope, context: WorkspaceRequestContext, force: Boolean = false) {
        store.ensure(scope, context, force)
    }

    /** Compatibility for non-owned chrome; refuses if the slug is not the active context. */
    fun ensure(scope: CoroutineScope, workspace: String?) {
        val context = WorkspaceRequestContext.current()?.takeIf { it.workspace.slug == workspace } ?: return
        store.ensure(scope, context)
    }

    fun invalidate() = store.invalidate()

    internal fun invalidate(context: WorkspaceRequestContext) = store.invalidate(context)

    internal fun seed(context: WorkspaceRequestContext, project: ProjectDetailDto): Boolean = store.seed(context, project)

    fun replace(project: ProjectDetailDto) = store.replace(project)

    fun find(slug: String?): ProjectSummaryDto? = store.find(slug)

    internal fun canAuthorize(context: WorkspaceRequestContext): Boolean = store.canAuthorize(context)

    internal fun canVerifyAllowlist(context: WorkspaceRequestContext): Boolean =
        store.canVerifyAllowlist(context)
}

private fun ProjectDetailDto.toSummary() = ProjectSummaryDto(
    id = id,
    orgId = orgId,
    orgSlug = orgSlug,
    name = name,
    slug = slug,
    sourceLocale = sourceLocale,
    createdAt = createdAt,
    updatedAt = updatedAt,
    kind = kind,
    iconKey = iconKey,
    accentKey = accentKey,
    hasCustomIcon = hasCustomIcon,
    description = description,
    styleGuide = styleGuide,
    locales = locales,
    progress = progress,
    keyCount = keyCount,
    fileCount = fileCount,
    localeCount = localeCount,
)
